/**
 * Twilio Voice SDK AudioProcessor that mixes a greeting recording into the
 * rep's outgoing mic audio, so the recipient hears it while the line stays
 * open in both directions. The rep hears the greeting locally as well.
 *
 * Register once per Device with `device.audio.addProcessor(mixer)`. The SDK
 * calls createProcessedStream/destroyProcessedStream around each call.
 */

const MONITOR_GAIN = 0.6;

export function createGreetingMixer() {
  let ctx = null;
  let inputStream = null;
  let micSource = null;
  let destination = null;
  let greetingBus = null;
  let monitorBus = null;
  let activeSource = null;
  let activeResolve = null;
  let playToken = 0;
  const bufferCache = new Map();

  const getContext = () => {
    if (!ctx || ctx.state === "closed") {
      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioContextCtor();
    }
    return ctx;
  };

  const loadBuffer = (url) => {
    if (!bufferCache.has(url)) {
      // no-store: the preview <audio> player may have cached this file from a
      // non-CORS request, and reusing that copy fails the CORS check.
      const pending = fetch(url, { mode: "cors", cache: "no-store" })
        .then((res) => {
          if (!res.ok) throw new Error(`Greeting fetch failed (${res.status})`);
          return res.arrayBuffer();
        })
        .then((data) => getContext().decodeAudioData(data));
      pending.catch(() => bufferCache.delete(url));
      bufferCache.set(url, pending);
    }
    return bufferCache.get(url);
  };

  const stop = () => {
    playToken += 1;
    const source = activeSource;
    const resolve = activeResolve;
    activeSource = null;
    activeResolve = null;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch (e) {
        // already stopped
      }
      source.disconnect();
    }
    resolve?.({ completed: false });
  };

  return {
    async createProcessedStream(stream) {
      const c = getContext();
      inputStream = stream;
      micSource = c.createMediaStreamSource(stream);
      destination = c.createMediaStreamDestination();
      greetingBus = c.createGain();
      monitorBus = c.createGain();
      monitorBus.gain.value = MONITOR_GAIN;
      micSource.connect(destination);
      greetingBus.connect(destination);
      monitorBus.connect(c.destination);
      return destination.stream;
    },

    async destroyProcessedStream() {
      stop();
      micSource?.disconnect();
      greetingBus?.disconnect();
      monitorBus?.disconnect();
      micSource = null;
      destination = null;
      greetingBus = null;
      monitorBus = null;
      inputStream = null;
    },

    /** Must run inside a user gesture (e.g. the Start Call click) at least once. */
    resume() {
      const c = getContext();
      if (c.state === "suspended") return c.resume().catch(() => {});
      return Promise.resolve();
    },

    /** Fetch and decode ahead of time so auto-play starts without delay. */
    preload(url) {
      return loadBuffer(url).then(
        () => true,
        () => false
      );
    },

    isCallActive() {
      return !!destination;
    },

    isPlaying() {
      return !!activeSource;
    },

    getContext() {
      return ctx;
    },

    /** The rep's mic stream for the current call (pre-mix), or null. */
    getInputStream() {
      return inputStream;
    },

    /**
     * Play `url` to both parties. Resolves `{ completed: true }` when it
     * finishes, or `{ completed: false }` if stopped early.
     */
    async play(url) {
      if (!destination) throw new Error("No active call to play the greeting into");
      stop();
      const token = playToken;
      const buffer = await loadBuffer(url);
      if (!destination) throw new Error("Call ended before the greeting loaded");
      const c = getContext();
      if (c.state === "suspended") await c.resume();
      if (token !== playToken) return { completed: false };
      const source = c.createBufferSource();
      source.buffer = buffer;
      source.connect(greetingBus);
      source.connect(monitorBus);
      return new Promise((resolve) => {
        activeSource = source;
        activeResolve = resolve;
        source.onended = () => {
          if (activeSource !== source) return;
          activeSource = null;
          activeResolve = null;
          source.disconnect();
          resolve({ completed: true });
        };
        source.start();
      });
    },

    stop,

    async close() {
      await this.destroyProcessedStream();
      bufferCache.clear();
      if (ctx && ctx.state !== "closed") {
        await ctx.close().catch(() => {});
      }
      ctx = null;
    },
  };
}
